/**
 * Eureka Report streaming parser.
 *
 * The report is bracketed by `<<<EUREKA_REPORT>>>` / `<<<END_EUREKA_REPORT>>>`
 * inside the ai_thinking text stream. We derive the split from the full raw text
 * on every change (covers live streaming and reload), and trim incomplete marker
 * prefixes that arrive split across SSE chunks (e.g. `<<<`, `EUR`, `KA_REPORT>>>`).
 */

/** Report start marker. */
export const EUREKA_REPORT_MARKER = '<<<EUREKA_REPORT>>>';

/** Report end marker. Present once the report has finished streaming. */
export const EUREKA_REPORT_END_MARKER = '<<<END_EUREKA_REPORT>>>';

/** All eureka markers, used for incomplete trailing-prefix detection. */
const EUREKA_MARKERS = [EUREKA_REPORT_MARKER, EUREKA_REPORT_END_MARKER];

/** Result of splitEurekaReport. */
export interface EurekaReportSplit {
  /** Chat text before the report start marker. */
  before: string;
  /** Chat text after the report end marker; '' until the report finishes. */
  after: string;
  /** Content between the markers (the report); null when there is no report. */
  report: string | null;
  /** Whether a complete report start marker was detected. */
  hasReport: boolean;
  /** True while the report is streaming (start marker present, end marker not yet). */
  generating: boolean;
}

/** Trim an incomplete `<<<...>>>` marker prefix from the end of the text. */
function stripTrailingPartialMarker(text: string): string {
  const lastTripleIdx = text.lastIndexOf('<<<');
  if (lastTripleIdx === -1) return text;

  const tail = text.slice(lastTripleIdx);
  // tail is a true prefix of a full marker (and not the full marker itself).
  const isIncompletePrefix = EUREKA_MARKERS.some((m) => m.startsWith(tail) && tail !== m);
  return isIncompletePrefix ? text.slice(0, lastTripleIdx) : text;
}

/** Split raw ai_thinking text into chat text and report content, preserving order. */
export function splitEurekaReport(raw: string | undefined | null): EurekaReportSplit {
  if (!raw) {
    return { before: '', after: '', report: null, hasReport: false, generating: false };
  }

  const startIdx = raw.indexOf(EUREKA_REPORT_MARKER);

  // No complete start marker: plain text (minus any incomplete trailing prefix).
  if (startIdx === -1) {
    return {
      before: stripTrailingPartialMarker(raw),
      after: '',
      report: null,
      hasReport: false,
      generating: false,
    };
  }

  const contentStart = startIdx + EUREKA_REPORT_MARKER.length;
  const before = raw.slice(0, startIdx);
  const endIdx = raw.indexOf(EUREKA_REPORT_END_MARKER, contentStart);

  // No end marker yet: report runs to the end, still streaming.
  if (endIdx === -1) {
    return {
      before: stripTrailingPartialMarker(before),
      after: '',
      report: raw.slice(contentStart),
      hasReport: true,
      generating: true,
    };
  }

  // End marker received: report sits between the markers; text after it returns to chat.
  return {
    before: stripTrailingPartialMarker(before),
    after: stripTrailingPartialMarker(raw.slice(endIdx + EUREKA_REPORT_END_MARKER.length)),
    report: raw.slice(contentStart, endIdx),
    hasReport: true,
    generating: false,
  };
}

/** Extract the first level-1 heading (`# xxx`) as the report title, else ''. */
export function getReportTitle(report: string | undefined | null): string {
  if (!report) return '';
  const match = report.match(/^\s*#\s+(.+?)\s*$/m);
  return match ? match[1].trim() : '';
}
